<template>
  <section class="py-16 md:py-24 bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900 relative overflow-hidden" id="delivery-app">
    <!-- Background Pattern -->
    <div class="absolute inset-0 opacity-10">
      <div class="absolute top-0 left-0 w-full h-full" 
           style="background-image: url('data:image/svg+xml,%3Csvg width=\'60\' height=\'60\' viewBox=\'0 0 60 60\' xmlns=\'http://www.w3.org/2000/svg\'%3E%3Cg fill=\'none\' fill-rule=\'evenodd\'%3E%3Cg fill=\'%23009B9D\' fill-opacity=\'0.4\'%3E%3Cpath d=\'M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z\'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E');">
      </div>
    </div>

    <!-- Floating Elements -->
    <div class="absolute top-20 left-10 w-20 h-20 bg-[#009B9D]/20 rounded-full blur-2xl animate-pulse"></div>
    <div class="absolute bottom-20 right-10 w-32 h-32 bg-[#009B9D]/10 rounded-full blur-3xl animate-pulse" style="animation-delay: 1s;"></div>

    <div class="w-[90%] mx-auto relative z-10">
      <div class="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
        <!-- Content -->
        <div class="text-center lg:text-left space-y-6">
          <!-- Coming Soon Badge -->
          <div class="inline-flex items-center space-x-2 bg-[#009B9D]/20 rounded-full px-4 py-2 border border-[#009B9D]/30">
            <span class="relative flex h-2 w-2">
              <span class="animate-ping absolute inline-flex h-full w-full rounded-full bg-[#009B9D] opacity-75"></span>
              <span class="relative inline-flex rounded-full h-2 w-2 bg-[#009B9D]"></span>
            </span>
            <span class="text-[#009B9D] text-sm font-medium">{{ $t('deliveryApp.comingSoon') }}</span>
          </div>

          <!-- Title -->
          <h2 class="text-3xl md:text-5xl font-bold text-white leading-tight">
            {{ $t('deliveryApp.title') }}
          </h2>

          <!-- Subtitle -->
          <p class="text-gray-400 text-lg max-w-md mx-auto lg:mx-0">
            {{ $t('deliveryApp.subtitle') }}
          </p>

          <!-- Features -->
          <div class="flex flex-wrap justify-center lg:justify-start gap-4 pt-4">
            <div class="flex items-center space-x-2 text-gray-300">
              <div class="w-8 h-8 rounded-full bg-[#009B9D]/20 flex items-center justify-center">
                <svg class="w-4 h-4 text-[#009B9D]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z"/>
                </svg>
              </div>
              <span class="text-sm">{{ $t('deliveryApp.features.earnings') }}</span>
            </div>
            <div class="flex items-center space-x-2 text-gray-300">
              <div class="w-8 h-8 rounded-full bg-[#009B9D]/20 flex items-center justify-center">
                <svg class="w-4 h-4 text-[#009B9D]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"/>
                </svg>
              </div>
              <span class="text-sm">{{ $t('deliveryApp.features.schedule') }}</span>
            </div>
            <div class="flex items-center space-x-2 text-gray-300">
              <div class="w-8 h-8 rounded-full bg-[#009B9D]/20 flex items-center justify-center">
                <svg class="w-4 h-4 text-[#009B9D]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M18.364 5.636l-3.536 3.536m0 5.656l3.536 3.536M9.172 9.172L5.636 5.636m3.536 9.192l-3.536 3.536M21 12a9 9 0 11-18 0 9 9 0 0118 0z"/>
                </svg>
              </div>
              <span class="text-sm">{{ $t('deliveryApp.features.support') }}</span>
            </div>
          </div>

          <!-- Email Signup -->
          <div class="pt-6">
            <form @submit.prevent="submitPhone" class="flex flex-col sm:flex-row gap-3 max-w-md mx-auto lg:mx-0">
              <div class="relative flex-grow">
                <input
                  v-model="phone"
                  type="tel"
                  :placeholder="$t('deliveryApp.placeholder')"
                  class="w-full px-5 py-4 bg-white/10 border border-white/20 rounded-xl text-white placeholder-gray-400 focus:outline-none focus:border-[#009B9D] focus:ring-2 focus:ring-[#009B9D]/30 transition-all duration-300"
                  required
                />
              </div>
              <button 
                type="submit"
                class="px-6 py-4 bg-[#009B9D] text-white font-semibold rounded-xl shadow-lg shadow-[#009B9D]/30 hover:shadow-xl hover:shadow-[#009B9D]/40 hover:-translate-y-0.5 transition-all duration-300 whitespace-nowrap"
                :disabled="isSubmitting"
              >
                <span v-if="!isSubmitting">{{ $t('deliveryApp.notify') }}</span>
                <span v-else class="flex items-center space-x-2">
                  <svg class="animate-spin h-5 w-5" fill="none" viewBox="0 0 24 24">
                    <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
                    <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                </span>
              </button>
            </form>
            
            <!-- Success Message -->
            <transition
              enter-active-class="transition duration-300 ease-out"
              enter-from-class="opacity-0 translate-y-2"
              enter-to-class="opacity-100 translate-y-0"
              leave-active-class="transition duration-200 ease-in"
              leave-from-class="opacity-100 translate-y-0"
              leave-to-class="opacity-0 -translate-y-2"
            >
              <p v-if="submitted" class="mt-3 text-[#009B9D] text-sm flex items-center justify-center lg:justify-start space-x-2">
                <svg class="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                  <path fill-rule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clip-rule="evenodd"/>
                </svg>
                <span>{{ $t('deliveryApp.successMessage') }}</span>
              </p>
            </transition>
          </div>
        </div>

        <!-- Illustration -->
        <div class="hidden lg:flex justify-center items-center relative">
          <!-- Phone Mockup -->
          <div class="relative">
            <!-- Glow Effect -->
            <div class="absolute -inset-4 bg-[#009B9D]/20 rounded-[4rem] blur-2xl"></div>
            
            <!-- Phone -->
            <div class="relative w-[240px] h-[480px] bg-gradient-to-b from-gray-800 to-gray-900 rounded-[3rem] p-2 shadow-2xl border border-gray-700">
              <div class="w-full h-full bg-gradient-to-br from-gray-100 to-gray-200 rounded-[2.5rem] flex items-center justify-center relative overflow-hidden">
                <!-- Placeholder Content -->
                <div class="text-center p-6">
                  <div class="w-20 h-20 mx-auto bg-[#009B9D]/10 rounded-2xl flex items-center justify-center mb-4">
                    <svg class="w-10 h-10 text-[#009B9D]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="1.5" d="M9 17a2 2 0 11-4 0 2 2 0 014 0zM19 17a2 2 0 11-4 0 2 2 0 014 0z"/>
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="1.5" d="M13 16V6a1 1 0 00-1-1H4a1 1 0 00-1 1v10a1 1 0 001 1h1m8-1a1 1 0 01-1 1H9m4-1V8a1 1 0 011-1h2.586a1 1 0 01.707.293l3.414 3.414a1 1 0 01.293.707V16a1 1 0 01-1 1h-1m-6-1a1 1 0 001 1h1M5 17a2 2 0 104 0m-4 0a2 2 0 114 0m6 0a2 2 0 104 0m-4 0a2 2 0 114 0"/>
                    </svg>
                  </div>
                  <h3 class="font-bold text-gray-800 text-lg">Kline Driver</h3>
                  <p class="text-gray-500 text-sm mt-2">Bientôt disponible</p>
                  
                  <!-- Animated dots -->
                  <div class="flex justify-center space-x-1 mt-4">
                    <span class="w-2 h-2 bg-[#009B9D] rounded-full animate-bounce" style="animation-delay: 0s;"></span>
                    <span class="w-2 h-2 bg-[#009B9D] rounded-full animate-bounce" style="animation-delay: 0.1s;"></span>
                    <span class="w-2 h-2 bg-[#009B9D] rounded-full animate-bounce" style="animation-delay: 0.2s;"></span>
                  </div>
                </div>
              </div>
            </div>

            <!-- Floating Cards -->
            <div class="absolute -left-16 top-1/4 bg-white rounded-xl shadow-xl p-3 animate-float" style="animation-delay: 0s;">
              <div class="flex items-center space-x-2">
                <div class="w-8 h-8 bg-green-100 rounded-full flex items-center justify-center">
                  <span class="text-green-600 text-xs font-bold">+5k</span>
                </div>
                <span class="text-xs font-medium text-gray-700">XAF earned</span>
              </div>
            </div>

            <div class="absolute -right-12 bottom-1/3 bg-white rounded-xl shadow-xl p-3 animate-float" style="animation-delay: 0.5s;">
              <div class="flex items-center space-x-2">
                <div class="w-8 h-8 bg-[#009B9D]/20 rounded-full flex items-center justify-center">
                  <svg class="w-4 h-4 text-[#009B9D]" fill="currentColor" viewBox="0 0 20 20">
                    <path fill-rule="evenodd" d="M5.05 4.05a7 7 0 119.9 9.9L10 18.9l-4.95-4.95a7 7 0 010-9.9zM10 11a2 2 0 100-4 2 2 0 000 4z" clip-rule="evenodd"/>
                  </svg>
                </div>
                <span class="text-xs font-medium text-gray-700">New delivery</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </section>
</template>

<script>
import { ref } from 'vue'

export default {
  name: 'DeliveryAppSection',
  setup() {
    const phone = ref('')
    const isSubmitting = ref(false)
    const submitted = ref(false)

    const submitPhone = () => {
      if (!phone.value) return

      isSubmitting.value = true

      const message = encodeURIComponent(`Bonjour, je souhaite être notifié au lancement de l'application livreur Kline. Mon numéro: ${phone.value}`)
      window.open(`https://wa.me/237682696414?text=${message}`, '_blank')

      isSubmitting.value = false
      submitted.value = true
      phone.value = ''

      // Reset after 3 seconds
      setTimeout(() => {
        submitted.value = false
      }, 3000)
    }

    return {
      phone,
      isSubmitting,
      submitted,
      submitPhone
    }
  }
}
</script>

<style scoped>
@keyframes float {
  0%, 100% {
    transform: translateY(0px);
  }
  50% {
    transform: translateY(-10px);
  }
}

.animate-float {
  animation: float 3s ease-in-out infinite;
}
</style>